import { Box, Typography } from "@mui/material";
import clsx from "clsx";
import { useState } from "react";
import { CrownIcon } from "../../../../assets/images/icons/CrownIcon";
import { DropDownItem } from "../../../../components/common/BriaDropDown/BriaDropDown";
import { useAppStore } from "../../../../hooks/useStores";
import { OrgFeatures } from "../../../../models/billing";
import { isBriaMember } from "../../../../utils";
import { TgModel } from "../store/tg-model-store";
import styles from "./NewTgDropdown.module.scss";

const useTgDropdown = () => {
	const { authStore, tgStore } = useAppStore();
	const [loadingCreateItems, setLoadingCreateItems] = useState(false);

	const createDropdownItems = async (
		alwaysRefresh = false,
		projectId?: string,
		cacheOnly = false,
	): Promise<DropDownItem[]> => {
		setLoadingCreateItems(true);

		const createItem = (model: TgModel): DropDownItem => ({
			value: model.id.toString(),
			key: model.name,
			extraData: model,
			icon:
				!isBriaMember(authStore.user) && !authStore.isFeatureEnabled(OrgFeatures.TAILORED_GENERATION) ? (
					<CrownIcon />
				) : (
					""
				),
			customItem: (
				<Box key={model.id} className={styles.TGItem}>
					<Box className={styles.itemWrap}>
						<Box className={styles.itemText}>{model.name}</Box>
						<Box className={clsx(styles.badgeClassName, styles.badge)}>{model.training_version}</Box>
					</Box>
					{model.description && (
						<Typography className={styles.optionsDescription}>{model.description}</Typography>
					)}
				</Box>
			),
		});

		try {
			if (projectId) {
				if (cacheOnly || (tgStore.tgProjectStore.paginatedModels.items.length && !alwaysRefresh)) {
					return tgStore.tgProjectStore.paginatedModels.items
						.filter((model) => model.status === "Completed")
						.map(createItem);
				}

				const models = await tgStore.tgProjectStore.getModels(Number(projectId), undefined, {
					filters: { status: "Completed" },
				});
				if (!models) return [];

				return models.items.map(createItem);
			} else {
				if (cacheOnly || (tgStore.tgModelStore.modelsList?.length && !alwaysRefresh)) {
					return (tgStore.tgModelStore.modelsList ?? []).map((model) => createItem(model));
				}

				const loadedModels = await tgStore.tgModelStore.listAllModels("Completed", true);
				if (!loadedModels) return [];

				return loadedModels.map((model) => createItem(model));
			}
		} catch (error) {
			console.error("Error loading tailored models:", error);
			return [];
		} finally {
			setLoadingCreateItems(false);
		}
	};

	return {
		createDropdownItems,
		loadingCreateItems,
	};
};

export default useTgDropdown;
